/* verqo — Seller Solutions page content (9 pages · shipped live Feb 2026)
   3 personas (brands / distributors / 3pls) + 6 industries
   Same catalog pattern as product pages — rich features, real portal
   screenshots framed by persona/industry, unique visuals per page.
*/
import {
  Building2, Warehouse, Sparkles, Shirt, UtensilsCrossed, Home, Heart, ShoppingBasket,
  Package, Users, TrendingUp, Truck, ShieldCheck, Layers, Zap, Store,
  LayoutGrid, Landmark, Banknote, Receipt, Bot, Shield, Rocket, DollarSign,
  Briefcase, LineChart, Wallet, Boxes as BoxesIcon, Clock, Gauge, PieChart,
  Percent, BadgeDollarSign, MessageSquare, ChartBar, Bell,
} from "lucide-react";
import { BrowserFrame } from "../../../../components/marketing/SubPageBlocks";

const CTA_PRIMARY = { label: "Get started", href: "/waitlist?as=seller" };

const SHOWCASE_CTA = {
  primary:   { label: "Get started",      href: "/waitlist?as=seller" },
  secondary: { label: "See how it works", href: "/company/how-it-works" },
};

const commonCta = (title) => ({
  title: title || "Ready to run your wholesale business on one platform?",
  subtitle: "Import your buyer list, upload a catalog, ship your first invoice in 48 hours.",
  primary: CTA_PRIMARY,
  secondary: { label: "Talk to a founder", href: "/company/contact" },
});

const REL = (slug, label, desc) => ({ href: `/solutions/${slug}`, label, desc });
const RELIND = (slug, label, desc) => ({ href: `/solutions/industry/${slug}`, label, desc });

const IMG = {
  catalogGrid:   "/preview-screens/portal-catalog/catalog-grid.jpeg",
  catalogList:   "/preview-screens/portal-catalog/catalog-list.jpeg",
  catalogAdmin:  "/preview-screens/portal-catalog/catalog-admin.jpeg",
  dashboard:     "/preview-screens/portal-dashboard/dashboard.jpeg",
  ordersAll:     "/preview-screens/portal-orders/all.jpeg",
  ordersDropship:"/preview-screens/portal-orders/dropship.jpeg",
  walletOverview:"/preview-screens/portal-wallet/overview.jpeg",
  walletCredit:  "/preview-screens/portal-wallet/credit.jpeg",
  invoicesMain:  "/preview-screens/portal-invoices/main.jpeg",
  dropshipMain:  "/preview-screens/portal-dropship/main.jpeg",
  buyersMain:    "/preview-screens/portal-buyers/main.jpeg",
  insuranceMain: "/preview-screens/portal-insurance/main.jpeg",
};

const URL = (path) => `app.verqo.com${path}`;

/* ─────────── PERSONAS ─────────── */

const brands = {
  eyebrow: "Solutions · Brands",
  title: "For DTC brands finally moving into wholesale.",
  subtitle: "You built a great product and a great following. Now the retailers are calling. Verqo is the one platform that gets you from linesheet PDF to a real wholesale business — without hiring a team.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.catalogGrid} alt="Verqo storefront — a DTC brand's wholesale front-door" url="sellers.verqo.com/s/your-brand/catalog" />,
  features: {
    heading: "Everything a founder-led brand needs on day one.",
    sub: "Verqo replaces the Slack channel between you and a rep, the QuickBooks scramble, the shipping tool, and the ten spreadsheets — with one login, one truth.",
    items: [
      { icon: LayoutGrid,    title: "Linesheet PDF killer",   desc: "Send buyers a real storefront, not a 40MB attachment. Live pricing, live stock, live promos." },
      { icon: TrendingUp,    title: "Get paid, day one",      desc: "Same-day cash even when retailers pay Net-60. Verqo funds the invoice within 24 hours." },
      { icon: ShieldCheck,   title: "Terms without risk",     desc: "Say yes to Net-30 without carrying it on your balance sheet. Allianz-backed." },
      { icon: Zap,           title: "No ops team required",   desc: "Verqo runs invoicing, shipping, and claims by default." },
      { icon: Bot,           title: "AI-run catalog imports", desc: "Drop in your Shopify export or PDF linesheet — catalog live in 2 minutes." },
      { icon: Rocket,        title: "48-hour onboarding",     desc: "Import buyer list, upload catalog, ship first invoice in under 48 hours." },
    ],
  },
  screenshots: {
    heading: "The brand-side wholesale-OS.",
    sub: "Everything a DTC brand needs to run a real wholesale channel — catalog, orders, payments, credit — under one login.",
    items: [
      { label: "Catalog",  src: IMG.catalogAdmin,   alt: "Seller catalog admin — 500 SKUs", url: URL("/seller/catalog"), caption: "Catalog admin — 500 SKUs across categories, brand-side pricing tiers, and per-buyer MOQ rules." },
      { label: "Orders",   src: IMG.ordersAll,      alt: "Unified orders queue",             url: URL("/seller/orders"),  caption: "910 orders across wholesale + dropship, one queue, one workflow." },
      { label: "Payments", src: IMG.walletOverview, alt: "Wallet + credit overview",         url: URL("/seller/wallet"),  caption: "Money in, money out, wallet, credit line — the CFO dashboard." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "From 3 accounts to 300",
      title: "Scale from indie darling to national brand — without hiring a wholesale ops team.",
      body: "Verqo replaces the manual glue between your systems and your buyers. Automated invoicing, embedded payments, and dropship rails so you can add 30 retailers in a month, not a quarter.",
      visual: <BrowserFrame src={IMG.dropshipMain} alt="Dropship Automation — the retailer-side flow" url={URL("/seller/dropship")} />,
    },
  ],
  quote: {
    quote: "We tripled wholesale revenue in 6 months without hiring anyone. Verqo is basically our wholesale ops team.",
    author: "Zoe M.", role: "Founder", company: "an indie skincare brand",
  },
  related: [
    REL("distributors", "Distributors", "For multi-line wholesalers."),
    REL("3pls",         "3PLs",         "For fulfillment partners embedding Verqo."),
    RELIND("beauty",    "Beauty",       "Deep beauty-industry integrations."),
  ],
  cta: commonCta(),
};

const distributors = {
  eyebrow: "Solutions · Distributors",
  title: "For distributors running 500+ SKUs and 500+ retailers.",
  subtitle: "The DMS you inherited was written in 1998. Verqo is the modern portal your brands demand and your retailers actually want to use — with EDI, multi-brand splits, and freight built-in.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.catalogAdmin} alt="Multi-brand catalog admin — 500 SKUs across lines" url={URL("/seller/catalog")} />,
  features: {
    heading: "A DMS you'll actually enjoy using.",
    sub: "Multi-brand, multi-warehouse, multi-currency — with EDI, freight, and credit built into the same portal your buyers use to reorder.",
    items: [
      { icon: Building2,     title: "Multi-brand support",     desc: "Manage 20+ lines from one dashboard with per-brand splits, per-brand payouts." },
      { icon: Layers,        title: "Tiered pricing at scale", desc: "Rules-based tiers for hundreds of retailers — no manual pricing sheets." },
      { icon: Users,         title: "Rep-friendly",            desc: "Reps see their book, quotas, and open opportunities. Order-on-behalf built in." },
      { icon: Truck,         title: "Freight + parcel unified", desc: "Every shipping move in one queue — parcel, LTL, FTL, drayage." },
      { icon: Package,       title: "EDI 850/810/856/855",     desc: "Full EDI support for enterprise retailers — Target, Walmart, Nordstrom." },
      { icon: DollarSign,    title: "Multi-brand payouts",     desc: "Split invoice proceeds across brands automatically per contract." },
    ],
  },
  screenshots: {
    heading: "Everything a distributor needs, in one console.",
    items: [
      { label: "Multi-brand catalog", src: IMG.catalogAdmin,   alt: "Catalog with brand filter",   url: URL("/seller/catalog"),        caption: "Filter by brand, category, or supplier. Manage 20+ lines from a single admin." },
      { label: "Unified orders",       src: IMG.ordersAll,      alt: "Orders queue — 910 orders",   url: URL("/seller/orders"),         caption: "910 orders across wholesale, dropship, and freight in one queue." },
      { label: "Buyer scoring",        src: IMG.buyersMain,     alt: "Buyer scoring dashboard",     url: URL("/seller/buyers"),         caption: "Live credit health across every retailer — critical for distributor exposure management." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "Yes, we replace SAP B1 and NetSuite too",
      title: "Migrations that take weeks, not quarters.",
      body: "Our onboarding team does the heavy lifting — SKU mapping, customer import, EDI setup. You go live in weeks with all your data intact.",
      visual: <BrowserFrame src={IMG.dashboard} alt="Analytics dashboard for a multi-brand distributor" url={URL("/seller")} />,
    },
  ],
  related: [
    REL("brands", "Brands", "For DTC brands moving into wholesale."),
    REL("3pls",   "3PLs",   "For fulfillment partners."),
    RELIND("cpg", "CPG",    "CPG-specific integrations."),
  ],
  cta: commonCta("Modernize your distribution business without a 2-year rip-and-replace."),
};

const threePls = {
  eyebrow: "Solutions · 3PLs",
  title: "For 3PLs embedding wholesale-native billing in your platform.",
  subtitle: "Give your brand customers a real portal for their retailers — with terms, credit, and instant payouts — while you take a cut. Verqo powers the money layer.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.dropshipMain} alt="Dropship Automation — the 3PL-embedded flow" url={URL("/seller/dropship")} />,
  features: {
    heading: "Wholesale money rails, white-labeled.",
    items: [
      { icon: Warehouse,     title: "White-label portal",     desc: "Your brand, your domain, your revenue share." },
      { icon: TrendingUp,    title: "Revenue share",           desc: "Take a share on every transaction, invoice, and financing spread." },
      { icon: ShieldCheck,   title: "Compliance handled",       desc: "MTL, KYB, AML — all covered by Verqo's sponsor bank." },
      { icon: Zap,           title: "API-first integration",    desc: "Embed in your existing WMS in weeks, not quarters." },
    ],
  },
  screenshots: {
    heading: "The 3PL money layer.",
    items: [
      { label: "Dropship",  src: IMG.dropshipMain,   alt: "Fulfillment center white-labeled", url: URL("/seller/dropship"),  caption: "Fulfillment Center — white-label the entire dropship stack for your brand customers." },
      { label: "Invoicing", src: IMG.invoicesMain,   alt: "Invoices auto-generated per shipment", url: URL("/seller/invoices"), caption: "Auto-invoicing tied to your WMS ship events — no more manual billing runs." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "For fulfillment partners",
      title: "Make more money on the invoices you already process.",
      body: "Layer instant payouts and Net-terms on top of your existing 3PL relationship. Brands stay with you longer. You collect margin on flows you never touched before.",
      visual: <BrowserFrame src={IMG.walletOverview} alt="Wallet + revenue share dashboard" url={URL("/seller/wallet")} />,
    },
  ],
  related: [
    REL("brands", "Brands", "For DTC brands going into wholesale."),
    REL("distributors", "Distributors", "For multi-line wholesalers."),
    RELIND("apparel","Apparel", "Apparel-specific 3PL flows."),
  ],
  cta: commonCta("Add a revenue line without adding an ops line."),
};

/* ─────────── INDUSTRIES ─────────── */

const industryFAQ = (industry) => [
  { q: `Do you support ${industry}-specific compliance?`, a: `Yes. Verqo handles the paperwork wholesalers actually need — from certificates and lot tracking to expiry and hazmat where applicable to ${industry}.` },
  { q: "Do I need to migrate SKUs?", a: "No. Verqo keeps your existing SKUs, UPCs, and GTINs. Import via CSV, Shopify, or our team-assisted onboarding." },
];

/* Screenshot rotation per industry — same portal, different frames */
const INDUSTRY_HERO = {
  beauty:  IMG.catalogGrid,
  apparel: IMG.catalogList,
  food:    IMG.catalogAdmin,
  home:    IMG.ordersAll,
  health:  IMG.invoicesMain,
  cpg:     IMG.walletOverview,
};
const INDUSTRY_HERO_URL = {
  beauty:  "sellers.verqo.com/s/your-brand/catalog",
  apparel: "sellers.verqo.com/s/your-brand/catalog",
  food:    URL("/seller/catalog"),
  home:    URL("/seller/orders"),
  health:  URL("/seller/invoices"),
  cpg:     URL("/seller/wallet"),
};

const industryPage = (name, icon, slug, teaser) => ({
  eyebrow: `Solutions · ${name}`,
  title: `Verqo for ${name.toLowerCase()} wholesale.`,
  subtitle: teaser,
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={INDUSTRY_HERO[slug]} alt={`Verqo — ${name} wholesale workflow`} url={INDUSTRY_HERO_URL[slug]} />,
  features: {
    heading: `Built for the way ${name.toLowerCase()} brands and distributors actually sell.`,
    items: [
      { icon: Package,     title: "Category-aware catalog", desc: `Fields, tags, and search tuned for ${name.toLowerCase()}.` },
      { icon: TrendingUp,  title: "Get-paid-day-one",       desc: "Same-day cash even when retailers pay Net-60." },
      { icon: ShieldCheck, title: "Non-recourse terms",     desc: "Extend Net-30/60/90 without touching your balance sheet." },
      { icon: icon,        title: `${name} network`,        desc: `Cross-brand buyer signal across ${name.toLowerCase()} retailers.` },
      { icon: Truck,       title: "Freight + parcel",       desc: "Every shipping move in one queue with commercial pricing." },
      { icon: Bot,         title: "AI catalog import",      desc: `${name}-specific templates for images, categories, and SKUs.` },
    ],
  },
  screenshots: {
    heading: `${name} on Verqo — the whole flow.`,
    sub: `Catalog, orders, invoicing, credit — every piece of a modern ${name.toLowerCase()} wholesale business under one login.`,
    items: [
      { label: "Catalog",  src: IMG.catalogGrid,   alt: `${name} catalog view`, url: `sellers.verqo.com/s/your-brand/catalog`, caption: `Buyer-facing storefront — category filters and hero imagery tuned for ${name.toLowerCase()} merchandising.` },
      { label: "Orders",   src: IMG.ordersAll,     alt: `${name} orders queue`, url: URL("/seller/orders"),                    caption: `${name} orders across every channel — DTC-to-wholesale funnel visible end-to-end.` },
      { label: "Payments", src: IMG.walletOverview, alt: `${name} payments dashboard`, url: URL("/seller/wallet"),             caption: `Money in / out per SKU-category — see ${name.toLowerCase()} revenue vs cost live.` },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: `Made for ${name.toLowerCase()}`,
      title: `The ${name.toLowerCase()} wholesale channel, without the spreadsheets.`,
      body: `${name} has quirks. Verqo respects them — lot tracking, expiry, size runs, hazmat, or whatever your category actually cares about — while giving you a modern portal to run the money.`,
      visual: <BrowserFrame src={IMG.buyersMain} alt={`${name} buyer scoring — retailer health per account`} url={URL("/seller/buyers")} />,
    },
  ],
  related: [
    RELIND("beauty",   "Beauty",   "Prestige and indie beauty."),
    RELIND("apparel",  "Apparel",  "Fashion & accessories."),
    RELIND("food",     "Food",     "Specialty food & beverage."),
  ].filter(r => !r.href.endsWith(slug)),
  faq: industryFAQ(name.toLowerCase()),
  cta: commonCta(),
});

const industries = {
  beauty:  industryPage("Beauty",  Sparkles,        "beauty",  "Prestige, indie, and drugstore beauty brands moving product into every specialty and mass retailer."),
  apparel: industryPage("Apparel", Shirt,           "apparel", "Fashion, accessories, and footwear brands running seasonal drops into boutiques and chains."),
  food:    industryPage("Food",    UtensilsCrossed, "food",    "Specialty food, coffee, and beverage brands selling into grocery, cafés, and marketplaces."),
  home:    industryPage("Home",    Home,            "home",    "Home goods, furniture, and decor brands selling into design and specialty retail."),
  health:  industryPage("Health",  Heart,           "health",  "Wellness, supplements, and personal care brands moving into specialty and mass retail."),
  cpg:     industryPage("CPG",     ShoppingBasket,  "cpg",     "Consumer packaged goods at every scale — from indie challenger to established multi-line."),
};

/* ─────────── BY ROLE (wholesale-scoped only) ───────────
   Every role page here is explicitly scoped to the businesses Verqo
   serves — wholesale brands, distributors, and 3PLs in beauty, apparel,
   food, home, health, and CPG. We do NOT market as a generic CFO/CEO
   platform; the "wholesale" qualifier is intentional and visible.
*/
const REL_ROLE = (slug, label, desc) => ({ href: `/solutions/role/${slug}`, label, desc });

const AUDIENCE_LINE = "For teams at wholesale brands, distributors, and 3PLs in beauty, apparel, food, home, health, and CPG. Not built for enterprise CFOs, professional-services CEOs, or SaaS ops — this is wholesale-industry infrastructure.";

const founderCeo = {
  eyebrow: "By role · Founders & CEOs of wholesale brands",
  title: "The wholesale channel, without hiring a wholesale team.",
  subtitle: `Verqo replaces the ops, finance, and shipping tools a growing brand would normally hire 5–8 people to run. One login runs the whole channel — catalog, orders, invoicing, payments, terms, shipping, insurance. ${AUDIENCE_LINE}`,
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.dashboard} alt="Seller dashboard — the CEO's wholesale cockpit" url={URL("/seller")} />,
  features: {
    heading: "What a wholesale CEO actually needs on Monday morning.",
    sub: "Not a generic BI dashboard. The five numbers that decide whether this quarter is a win — plus one place to fix them.",
    items: [
      { icon: TrendingUp,    title: "Channel P&L at a glance",       desc: "Wholesale revenue, gross margin, DSO, and open exposure — refreshed every night, filterable by rep, buyer, or SKU." },
      { icon: Rocket,        title: "Ship in 48 hours",              desc: "Import buyer list, upload catalog, ship first invoice — a wholesale brand can go live in two days flat." },
      { icon: DollarSign,    title: "Cash without the runway story", desc: "Same-day cash on every invoice — no fundraise required just because a big retailer wants Net-60." },
      { icon: Users,         title: "Reps without the platform",     desc: "Your reps get a real tool, not a shared Google Sheet. You get the pipeline they used to hide in their inbox." },
      { icon: Shield,        title: "Every risk covered",             desc: "Buyer default, cargo damage, disputes — Allianz-backed cover embedded on every invoice and shipment." },
      { icon: Package,       title: "Retail-ready from day one",      desc: "EDI 850/810/855/856 with Target, Nordstrom, Whole Foods and every major national retailer — no six-figure integration bill." },
    ],
  },
  blocks: [
    {
      eyebrow: "The founder trap",
      title: "You built the brand. Now you're chasing 60 receivables on Friday night.",
      body: "Every wholesale brand hits the moment where growth demands ops discipline. Verqo gets you past that moment without hiring a $300K wholesale ops team — because it IS the ops team, just software.",
      visual: <BrowserFrame src={IMG.walletOverview} alt="Seller wallet — the financial cockpit for a wholesale founder" url={URL("/seller/wallet")} />,
    },
  ],
  screenshots: {
    heading: "The CEO's five dashboards, in one login.",
    items: [
      { label: "Dashboard",  src: IMG.dashboard,      alt: "CEO dashboard",         url: URL("/seller"),        caption: "The five numbers: revenue, GM, DSO, open exposure, WoW growth." },
      { label: "Payments",   src: IMG.walletOverview, alt: "Payments overview",     url: URL("/seller/wallet"), caption: "Every dollar in, every dollar out, live." },
      { label: "Buyers",     src: IMG.buyersMain,     alt: "Buyer health scoring",  url: URL("/seller/buyers"), caption: "Who's healthy, who's slipping, who to invest in." },
    ],
    cta: SHOWCASE_CTA,
  },
  faq: [
    { q: "Is this for any CEO?",  a: "No — Verqo is built specifically for CEOs of wholesale brands (beauty, apparel, food, home, health, CPG) and distributors moving physical product to retail buyers. It's not a horizontal ERP or a generic finance tool." },
    { q: "How is this different from a big-brand ERP like NetSuite?", a: "ERPs are configurable frameworks; Verqo is opinionated infrastructure for wholesale specifically. If you're a wholesale brand doing $2M–$200M, Verqo replaces the ERP, the payments stack, and the shipping tool with something purpose-built." },
  ],
  related: [
    REL_ROLE("cfo",               "CFOs & Controllers",  "For finance leaders at wholesale brands."),
    REL_ROLE("head-of-wholesale", "Head of Wholesale",   "For the wholesale channel owner."),
    REL_ROLE("head-of-ops",       "Head of Ops",         "For ops leaders."),
    REL("brands", "For brands", "The DTC-to-wholesale playbook."),
  ],
  cta: commonCta("Ready to run your wholesale business on one platform?"),
};

const cfo = {
  eyebrow: "By role · CFOs & Controllers of wholesale brands",
  title: "The AR/AP layer wholesale should have had years ago.",
  subtitle: `Cash-flow, DSO, aging, exposure, and reconciliation — one ledger, one export, examiner-ready from day one. ${AUDIENCE_LINE}`,
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.walletOverview} alt="CFO cockpit — cash-flow, DSO, aging" url={URL("/seller/wallet")} />,
  features: {
    heading: "What a wholesale CFO reads first thing Monday.",
    sub: "Not a generic BI cube. The numbers wholesale finance actually cares about — DSO, aging bands, buyer concentration, terms exposure, and cash on hand.",
    items: [
      { icon: LineChart,   title: "DSO by cohort, live",         desc: "See exactly which buyers, terms, and reps are dragging your DSO — not a 30-day-old QuickBooks report." },
      { icon: PieChart,    title: "Aging in 4 clicks",            desc: "0-30, 31-60, 61-90, 90+ — sortable by buyer, rep, or SKU. Export to CSV, XLSX, or GL-ready format." },
      { icon: BadgeDollarSign, title: "One reconciliation",       desc: "ACH, wire, card, and Verqo Advance — one deposit, one entry, one export to QuickBooks / NetSuite / Xero / Sage Intacct." },
      { icon: ShieldCheck, title: "Exposure caps enforced",       desc: "Set concentration limits by buyer, region, or credit band. Verqo enforces them at checkout — no more surprise 40% concentration on one account." },
      { icon: Landmark,    title: "Non-recourse terms",           desc: "Buyer default is Verqo's problem, not yours. Every extended-term invoice is Allianz-insured behind the scenes." },
      { icon: Percent,     title: "Priced per invoice",            desc: "1.5–3.5% per financed invoice. No annual, no monthly, no minimums, no lockups — clean line items your CFO can audit." },
    ],
  },
  blocks: [
    {
      eyebrow: "Audit-ready by default",
      title: "Every dollar traceable. Every event timestamped.",
      body: "Verqo's ledger is examiner-grade — every invoice, every credit event, every payment attempt is timestamped and immutable. Your SOC 2 audit and sponsor-bank review just got a lot shorter.",
      visual: <BrowserFrame src={IMG.walletCredit} alt="Credit exposure — per-buyer aging and utilization" url={URL("/seller/wallet#credit")} />,
    },
  ],
  faq: [
    { q: "Is this for CFOs in any industry?",       a: "No — Verqo is scoped to wholesale physical-goods CFOs (brands, distributors, 3PLs in beauty, apparel, food, home, health, CPG). If you're a SaaS or professional-services CFO, this is not built for you." },
    { q: "Does it replace QuickBooks / NetSuite?",  a: "No. Verqo sits above your accounting system as the wholesale-specific AR/AP layer and syncs GL entries down. Keep your books where they are." },
    { q: "Is buyer-terms extension on our balance sheet?", a: "No. Verqo funds the invoice and takes non-recourse risk. Your P&L reflects the day-1 cash, not a receivable." },
  ],
  related: [
    REL_ROLE("founder-ceo",       "Founders & CEOs",    "For the founder scaling the channel."),
    REL_ROLE("head-of-wholesale", "Head of Wholesale",  "For the P&L owner."),
    REL("distributors", "For distributors", "Multi-line distributor playbook."),
  ],
  cta: commonCta(),
};

const headOfWholesale = {
  eyebrow: "By role · Heads of Wholesale at brands",
  title: "The wholesale channel, without the spreadsheet gymnastics.",
  subtitle: `One workspace for the whole channel — buyer list, catalog, reps, orders, terms, credit, shipping — with real analytics per rep, per buyer, per SKU. ${AUDIENCE_LINE}`,
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.buyersMain} alt="Wholesale team — buyer directory & health" url={URL("/seller/buyers")} />,
  features: {
    heading: "The tools your reps actually want to open.",
    sub: "Not a CRM bolted onto a shipping tool. A native wholesale workspace — reps, buyers, catalog, and money in one login.",
    items: [
      { icon: Users,         title: "Rep book, real-time",         desc: "Every rep sees their pipeline, their commission, and their buyers — no more sales-Slack begging for status." },
      { icon: MessageSquare, title: "RFQ pipeline",                desc: "Big custom quotes handled without email ping-pong — structured, signable, one-click to invoice." },
      { icon: Store,         title: "White-label storefront",       desc: "Buyers reorder themselves at their own price tier. Reps freed up to open net-new accounts." },
      { icon: Landmark,      title: "Terms without asking finance", desc: "Reps can extend Net-30/60/90 at the checkout — with pre-approved buyer limits and no back-office ticket." },
      { icon: Gauge,         title: "Buyer health per rep",         desc: "See which reps are stacking cash and which are stacking exposure — coach accordingly." },
      { icon: ChartBar,      title: "SKU velocity by channel",       desc: "Which SKUs move in which channels, at which margin — data your buying team needs on Monday." },
    ],
  },
  blocks: [
    {
      eyebrow: "For the person on the hook",
      title: "You own the wholesale P&L. Verqo makes it visible.",
      body: "Instead of stitching Shopify, QuickBooks, ShipStation, HubSpot, and a Google Sheet — one workspace, one truth, one number to defend at QBRs.",
      visual: <BrowserFrame src={IMG.dashboard} alt="Wholesale dashboard — revenue, margin, DSO, growth" url={URL("/seller")} />,
    },
  ],
  faq: [
    { q: "Does this replace our CRM?", a: "For wholesale, yes. HubSpot / Salesforce make sense for a sales pipeline; Verqo owns the wholesale sales AND fulfillment AND money workflow — one system of record instead of five." },
    { q: "Is it wholesale-only?",       a: "Yes. Verqo is built for wholesale brands, distributors, and 3PLs in physical-goods industries. It's not built for services or generic B2B SaaS." },
  ],
  related: [
    REL_ROLE("founder-ceo",  "Founders & CEOs",   "For the founder scaling the channel."),
    REL_ROLE("cfo",          "CFOs & Controllers", "For finance leaders."),
    REL_ROLE("head-of-ops",  "Head of Ops",        "For the ops leader."),
  ],
  cta: commonCta(),
};

const headOfOps = {
  eyebrow: "By role · Heads of Ops at wholesale brands",
  title: "Kill the tab-switching. One workspace for orders, shipping, and claims.",
  subtitle: `Every order, every label, every claim, every invoice — one queue, one workflow, one team. ${AUDIENCE_LINE}`,
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.ordersAll} alt="Unified orders queue — 910 orders across every channel" url={URL("/seller/orders")} />,
  features: {
    heading: "What ops needs on a Wednesday afternoon.",
    sub: "Not another dashboard. A single queue that sequences the work — SLA counters, print buttons, and claim intake — for the next 30 orders your team is actually working on.",
    items: [
      { icon: BoxesIcon,     title: "Unified order queue",         desc: "Wholesale + dropship + DTC in one queue with per-channel routing rules — no more 3 tools for 3 flows." },
      { icon: Truck,         title: "Multi-carrier at bank rates",  desc: "USPS, UPS, FedEx, DHL, plus 40+ LTL carriers — commercial pricing negotiated for you, no minimums." },
      { icon: Zap,           title: "Batch labels & claims",         desc: "5,000 labels in an hour. Damage claims auto-filed the moment a buyer flags them." },
      { icon: Layers,        title: "SLA countdown per order",       desc: "Every order shows time-to-SLA-breach — sorted hottest at top so your team works the right order next." },
      { icon: Bot,           title: "Rules-based routing",           desc: "Route by SKU, region, buyer, or 3PL — no more copy-paste routing decisions on a Monday morning." },
      { icon: Bell,          title: "Alerts before things break",    desc: "Slack + email + in-app pings for stalled orders, aging claims, and low-stock SKUs — proactive, not reactive." },
    ],
  },
  blocks: [
    {
      eyebrow: "For the ops lead",
      title: "The team doubled. The tabs didn't.",
      body: "Verqo is the layer that keeps a 3-person ops team running like a 15-person one — because every workflow, from label printing to claims to invoicing, is inside the same portal.",
      visual: <BrowserFrame src={IMG.ordersDropship} alt="Dropship fulfillment — 380 orders across two retailers" url={URL("/seller/dropship")} />,
    },
  ],
  faq: [
    { q: "Does this replace ShipStation?", a: "Yes. Verqo Shipping is a cheaper ShipStation replacement — same multi-carrier rates, same batch labels, plus native B2B (invoicing, terms, claims) built in." },
    { q: "Is it for any ops team?",         a: "No — this is wholesale ops. If you're running a services back-office or a SaaS support desk, Verqo isn't for you." },
  ],
  related: [
    REL_ROLE("founder-ceo",       "Founders & CEOs",   "For the founder scaling the channel."),
    REL_ROLE("head-of-wholesale", "Head of Wholesale", "For the P&L owner."),
    REL("3pls", "For 3PLs", "For fulfillment partners embedding Verqo."),
  ],
  cta: commonCta(),
};

export const SELLER_SOLUTIONS = {
  brands, distributors, "3pls": threePls,
  "industry/beauty":   industries.beauty,
  "industry/apparel":  industries.apparel,
  "industry/food":     industries.food,
  "industry/home":     industries.home,
  "industry/health":   industries.health,
  "industry/cpg":      industries.cpg,
  "role/founder-ceo":        founderCeo,
  "role/cfo":                cfo,
  "role/head-of-wholesale":  headOfWholesale,
  "role/head-of-ops":        headOfOps,
};
